import type { Challenge } from 'altcha/types';
import ky from 'ky';

const assertActive = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('ALTCHA verification cancelled', 'AbortError');
  }
};

/** Solve locally; only Logto's server may submit/consume this proof for verification. */
export const solveAltcha = async (challengeUrl: string, controller: AbortController) => {
  const challenge = await ky
    .get(challengeUrl, {
      credentials: 'omit',
      cache: 'no-store',
      redirect: 'error',
      retry: 0,
      timeout: 10_000,
      signal: controller.signal,
    })
    .json<Challenge>();
  assertActive(controller.signal);

  // Bound KDF work before passing data from a remote service to Web Crypto.
  const { parameters } = challenge;
  if (
    parameters.algorithm !== 'PBKDF2/SHA-256' ||
    parameters.cost !== 5000 ||
    parameters.keyLength !== 32 ||
    Boolean(parameters.memoryCost) ||
    Boolean(parameters.parallelism) ||
    typeof parameters.expiresAt !== 'number' ||
    parameters.expiresAt <= Date.now() / 1000
  ) {
    throw new Error('Unsupported or expired ALTCHA challenge');
  }

  const { solveChallenge, pbkdf2 } = await import('altcha/lib');
  assertActive(controller.signal);
  const solution = await solveChallenge({
    challenge,
    deriveKey: pbkdf2.deriveKey,
    controller,
    counterMode: 'uint32',
    timeout: 30_000,
  });
  assertActive(controller.signal);
  if (!solution) {
    throw new Error('ALTCHA solving failed');
  }
  return JSON.stringify({ challenge, solution });
};
